import { ObterDadosParaInsercaoEdicaoParcelaResposta } from "app/api/classes/OrcamentoApi";
import microuniversoApi from "app/api/microuniversoApi";
import { renderizarComponentReact } from "app/components/renderizarOuRemoverComponentReact";
import { useAppSelector } from "app/redux/hooks";
import { PromiseQueue } from "PromiseQueue";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { IncluirEditarParcela } from "./IncluirEditarParcela";
import { encontrarTipoDocumentoPorTipoParcela } from "./encontrarTipoDocumentoPorTipoParcela"; // 489850
import * as R from 'ramda';
import { Parametro } from "app/types/Parametro";
import { obterParametros } from "app/use-cases/parametro/obterParametros";
import globalMU from "globalMU";
import { decodificar as decodificarCMC7 } from "app/types/CMC7";
import { TelaFormaDePagamentoContainer } from "../TelaFormaDePagamentoContainer";
import { tratamentoErroPadrao } from "app/use-cases";
import { DadosParcela } from "app/types/IncluirEditarParcela";

export type IncluirEditarParcelaContainerProps = {
    somenteLeitura: boolean;
    editandoPlanoPagamento: boolean;
    indiceParcela?: number;
    parcela: DadosParcela;
    onParcelaAtualizada: () => void;
};

export const IncluirEditarParcelaContainer = React.memo((props: IncluirEditarParcelaContainerProps) => {
    const orcamento = useAppSelector(state => state.orcamento);
    const [carregandoDados,setCarregandoDados] = useState(true)

    const [parcela, setParcela] = useState<DadosParcela>(props.parcela ?? {});
    const [promiseQueue] = useState(new PromiseQueue());

    const [dados, setDados] = useState<ObterDadosParaInsercaoEdicaoParcelaResposta>(null);
    const [permiteEditarValor, setPermiteEditarValor] = useState(false);
    const [parametros, setParametros] = useState<Parametro[]>([]);
    const [listaParcelasCartao, setListaParcelasCartao] = useState<number[]>([]);
    const [quantidadeParcelasCartaoOriginal, setQuantidadeParcelasCartaoOriginal] = useState(props.parcela?.QuantidadeParcelasCartao ?? 0);
    const [valorParcelasCartao, setValorParcelasCartao] = useState(0);

    // Função para atualizar o valor das parcelas do cartão
    const atualizarValorParcelasCartao = useCallback(() => {
        const tipoDocumentoSelecionado = encontrarTipoDocumentoPorTipoParcela(dados?.TiposDocumento ?? [], parcela.IdTipoDocumento, parcela.Tipo); // 489850

        const quantParcela = parcela.QuantidadeParcelasCartao ?? tipoDocumentoSelecionado?.QuantidadeMinimaParcelaPrazo ?? 0;
        if (parcela.QuantidadeParcelasCartao == null && quantParcela > 0) {
            parcela.QuantidadeParcelasCartao = quantParcela;
        }

        setValorParcelasCartao(
            (quantParcela ?? 0) == 0 ? 0 : Math.trunc((global.unformatNumber(parcela.Valor) / parcela.QuantidadeParcelasCartao) * 100) / 100
        );
    }, [dados?.TiposDocumento, parcela]);

    // Função para atualizar a lista de parcelas do cartão
    const atualizarListaParcelasCartao = useCallback(() => {
        setListaParcelasCartao([]);

        if (globalMU.isNullOrEmpty(parcela.IdTipoDocumento)) {
            return;
        }

        const tipoDocumentoSelecionado = encontrarTipoDocumentoPorTipoParcela(dados?.TiposDocumento ?? [], parcela.IdTipoDocumento, parcela.Tipo); // 489850

        if (tipoDocumentoSelecionado == null) {
            return;
        }

        if (tipoDocumentoSelecionado.Tipo != 17763 && tipoDocumentoSelecionado.Tipo != 16200) {
            return;
        }

        let listaParcelasCartaoConsiderandoMinimo = [];
        let inicial = tipoDocumentoSelecionado.QuantidadeMinimaParcelaVista == 0 ? 1 : tipoDocumentoSelecionado.QuantidadeMinimaParcelaVista;
        let final = tipoDocumentoSelecionado.QuantidadeMaximaParcelaVista;

        if (parcela.Tipo == 2) {
            inicial = tipoDocumentoSelecionado.QuantidadeMinimaParcelaPrazo == 0 ? 1 : tipoDocumentoSelecionado.QuantidadeMinimaParcelaPrazo;
            final = tipoDocumentoSelecionado.QuantidadeMaximaParcelaPrazo;
        }

        if (final == 0 || final < inicial) {
            final = inicial;
        }

        for (let i = inicial; i <= final; i++) {
            listaParcelasCartaoConsiderandoMinimo.push(i);
        }

        setListaParcelasCartao(listaParcelasCartaoConsiderandoMinimo);
    }, [dados?.TiposDocumento, parcela]);

    // Função para atualizar os dados da parcela
    const atualizarDados = useCallback(async () => {
        const dados_ = await microuniversoApi.Orcamento.obterDadosParaInsercaoEdicaoParcela(orcamento.IdentificadorOrcamento);
        setDados(dados_);

        setParcela(parcela => ({
            ...parcela,
            Valor: parcela.Valor ?? dados_.ValorRestanteParcela
        }));
    }, [orcamento.IdentificadorOrcamento]);

    // Função para obter os tipos de documento
    const obterTiposDocumento = useCallback(() => {
        return (dados?.TiposDocumento ?? []);
    }, [dados?.TiposDocumento]);

    // Função para renderizar as formas de pagamento
    const renderizarFormasDePagamento = useCallback(() => {
        renderizarComponentReact(
            <TelaFormaDePagamentoContainer
                somenteLeitura={orcamento.SomenteLeitura}
            />,
            "conteudo-pagina",
            true
        );
    }, [orcamento.SomenteLeitura]);

    // Função para atualizar a permissão de edição do valor
    const atualizarPermiteEditarValor = useCallback(() => {
        const ehUltimaParcela = () => props.indiceParcela == null || props.indiceParcela == orcamento.ParcelasFormaPagamento[orcamento.ParcelasFormaPagamento.length - 1].Index;

        if (orcamento == null || parcela == null) {
            setPermiteEditarValor(false);
            return;
        }

        setPermiteEditarValor(
            !props.editandoPlanoPagamento &&
            (orcamento.UtilizaPlanoPagamento
                || !orcamento.RecalcularParcelas
                || R.includes(orcamento.ParcelasFormaPagamento, [null, undefined])
                || (orcamento.ParcelasFormaPagamento != null && orcamento.RecalcularParcelas && ehUltimaParcela() && !orcamento.UtilizaPlanoPagamento)
                || (parcela.Tipo == 1 && orcamento.RecalcularParcelas && !orcamento.UtilizaPlanoPagamento))
        );
    }, [orcamento, parcela, props.editandoPlanoPagamento, props.indiceParcela]);

    // Função para carregar os parâmetros
    const carregarParametros = useCallback(async () => {
        setParametros(
            await obterParametros({
                nomes: [
                    "VENDA_RECEBER_ENTREGA"
                ]
            })
        );
    }, []);

    // Função para obter o valor de um parâmetro
    const valorParametro = useCallback((nome: string) => parametros?.find(p => p.Nome == nome)?.Valor, [parametros]);

    // Função para lidar com a modificação da parcela
    const onParcelaModificada = useCallback((parcelaModificada: DadosParcela) => {
        
        const dadosCMC7Decodificados = decodificarCMC7(parcelaModificada.CodigoCMC7);
        setParcela(parcela_ => ({
            ...parcela_,
            ...parcelaModificada,
            Vencimento: R.includes(parcelaModificada.Tipo, [0, 1]) ? new Date().toISOString() : undefined,
            NumeroBanco: dadosCMC7Decodificados.numeroBanco ?? parcelaModificada.NumeroBanco,
            NumeroAgencia: dadosCMC7Decodificados.numeroAgencia ?? parcelaModificada.NumeroAgencia,
            NumeroConta: dadosCMC7Decodificados.numeroConta ?? parcelaModificada.NumeroConta,
            NumeroDocumento: dadosCMC7Decodificados.numeroDocumento ?? parcelaModificada.NumeroDocumento
        }));
    }, []);

    // Função para atualizar a parcela
    const atualizarParcela = useCallback(async (parcela_: DadosParcela) => {
        
        setParcela(parcela_);
        await microuniversoApi.Orcamento.atualizarParcela(orcamento.IdentificadorOrcamento, [parcela_]);
        props.onParcelaAtualizada();
    }, [orcamento.IdentificadorOrcamento, props.onParcelaAtualizada]);

    // Função para validar o recebimento na entrega
    const onValidarRecebimentoEntrega = useCallback(async (_parcela: DadosParcela) => {
        try {
            globalMU.blockUI();

            const recebimentoNaEntregaValido = await microuniversoApi.Orcamento.validarReceberEntrega(orcamento.IdentificadorOrcamento);
            if (!recebimentoNaEntregaValido) {
                setParcela({ ..._parcela, RecebimentoNaEntrega: false });
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }, [orcamento.IdentificadorOrcamento]);

    // Função para validar a conta portadora e o tipo de documento
    const onValidarContaPortadorTipoDocumento = useCallback(async (_parcela: DadosParcela) => {
        try {
            globalMU.blockUI();

            const oidContaPortadorTipoDocumento = await microuniversoApi.Orcamento.validarContaPortadorTipoDocumento(orcamento.IdentificadorOrcamento, _parcela.IdTipoDocumento);
            if (oidContaPortadorTipoDocumento > 7) {
                setParcela({ ..._parcela, ContaPortadora: oidContaPortadorTipoDocumento });
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }, [orcamento.IdentificadorOrcamento]);

    // Efeito para carregar os dados iniciais
    useEffect(() => {
        promiseQueue.enqueue(atualizarDados);
        carregarParametros();
    }, []);

    // Efeito para atualizar permissões e listas quando a parcela muda
    useEffect(() => {
        const executarFuncoes = async () => {
            await Promise.all([
                atualizarPermiteEditarValor(),
                atualizarListaParcelasCartao(),
                atualizarValorParcelasCartao()
            ]);
        };

        executarFuncoes();
        setCarregandoDados(false);
    }, [parcela]);

    // Efeito para atualizar a lista de parcelas do cartão quando os dados mudam
    useEffect(() => {
        atualizarListaParcelasCartao();
    }, [dados]);

    return (
        carregandoDados ? null :
        <IncluirEditarParcela
            parcela={parcela}
            ativarRecebimentoPixAPrazo={orcamento.AtivarRecebimentoPixAPrazo} // 414608
            somenteLeitura={props.somenteLeitura}
            onValidarRecebimentoEntrega={onValidarRecebimentoEntrega}
            listaQuantidadeParcelas={listaParcelasCartao}
            contasPortadoras={dados?.ContasPortadoras ?? []}
            tiposParcelas={dados?.TiposParcelas ?? []}
            tiposDocumento={obterTiposDocumento()}
            desativarEdicaoValor={!permiteEditarValor}
            desativarSelecaoTipo={props.editandoPlanoPagamento}
            desativarEdicaoTipoDocumento={props.editandoPlanoPagamento}
            desativarEdicaoDataVencimento={props.editandoPlanoPagamento}
            exibirCheckboxReceberNaEntrega={valorParametro("VENDA_RECEBER_ENTREGA") == "S"}
            onParcelaModificada={onParcelaModificada}
            valorParcelasCartao={valorParcelasCartao}
            onValidarContaPortadorTipoDocumento={onValidarContaPortadorTipoDocumento}
            onSelecionarDataVencimento={() => {}}
            onGerarlistaQuantidadeParcelas={() => {}}
            onAplicar={atualizarParcela}
            onErrorValidacao={() => {}}
        />
    );
});